/**
 * Шифрування вмісту записів ключем свого запису — [ADR-0016].
 *
 * Тут же живе перехід зі старого стану. До появи акаунтів вміст шифрувався
 * ключем пристрою (алгоритм 3, [ADR-0015]); тепер він шифрується ключем,
 * виведеним із ключа даних (алгоритм 1). У базі якийсь час лежатиме
 * і те, і те.
 *
 * Тому `open` дивиться на байт алгоритму й вибирає, чим відкривати, а
 * `seal` завжди пише новим. З цього випливає властивість, заради якої так
 * і зроблено: **перешифрування не потребує ніякого обліку прогресу**.
 * Байт алгоритму в кожному блобі і є позначкою, зроблений він чи ні.
 * Обірвати міграцію можна будь-якої миті — застосунок працює далі, а
 * наступний прогін продовжить з того, що лишилося.
 */
import {
  Algorithm,
  type CipherPort,
  type ContentCipherPort,
  type DataKey,
  DecryptionFailedError,
  decodeEnvelope,
  deriveEntryKey,
  openAead,
  sealAead,
} from "@beresta/core";

/**
 * Ключ даних приходить функцією, а не значенням.
 *
 * Причина проста: він зʼявляється пізніше, ніж будується контекст. База
 * піднімається до онбордингу — інакше не було б куди писати, — а ключ
 * даних отримується вже після того, як людина ввела фразу. Перебудовувати
 * заради цього весь контекст означало б перестворювати репозиторії під
 * екранами, які саме на них дивляться.
 *
 * `null` — акаунта ще немає: вміст шифрується сейфом пристрою, як у M1.
 * Це не «шифрування вимкнене», а попередній стан того самого архіву, і він
 * має лишатися робочим, поки користувач не завів акаунт.
 */
export function createContentCipher(
  device: CipherPort,
  dataKey: () => DataKey | null,
): ContentCipherPort {
  return {
    async seal(plaintext, entityId) {
      const dek = dataKey();
      if (!dek) return device.seal(plaintext);
      return sealAead(deriveEntryKey(dek, entityId), plaintext);
    },

    async open(sealed, entityId) {
      const dek = dataKey();
      const algorithm = decodeEnvelope(sealed).algorithm;

      if (algorithm === Algorithm.AesGcm256) {
        // Блоб, записаний до появи акаунта. Сейф пристрою нікуди не
        // подівся — саме тому міграцію можна робити ліниво, а не одним
        // непереривним прогоном.
        return device.open(sealed);
      }

      if (algorithm === Algorithm.XChaCha20Poly1305) {
        if (!dek) {
          // Такий блоб міг приїхати лише синхронізацією або лишитися від
          // акаунта, з якого вийшли. Мовчати тут не можна: інакше запис
          // виглядав би порожнім, а не замкненим.
          throw new DecryptionFailedError("запис зашифрований ключем акаунта, а акаунт замкнено");
        }
        return openAead(deriveEntryKey(dek, entityId), sealed);
      }

      throw new DecryptionFailedError(`алгоритм ${algorithm} не для вмісту записів`);
    },
  };
}
